// CP-4 — useBustNotifications hook.
//
// Reads unacknowledged bust notifications for the signed-in user and
// exposes an acknowledge action backed by the SECURITY DEFINER RPC
// public.acknowledge_bust_notification(p_notification_id uuid) from Slice α.
//
// Notifications carry the capital_after balance so the UI can render
// the honest post-bust position (capital unchanged vs drawn) without
// leaking methodology.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuthSession } from "../auth/useAuthSession.ts";
import { getBrowserClient } from "../supabase/browser.ts";

export interface ScBustNotification {
  id: string;
  chain_id: string;
  reserve_id: string;
  session_id: string;
  bust_game: number;                       // 1 | 2 | 3
  stake_lost: number;
  stake_source: "capital" | "profit";
  capital_after: number;
  profit_after: number;
  message: string;
  cooldown_ends_at: string | null;         // ISO8601 if joined later
  created_at: string;
  acknowledged_at: string | null;
}

export interface UseBustNotificationsState {
  notifications: readonly ScBustNotification[];
  loading: boolean;
  error: string | null;
  acknowledge: (id: string) => Promise<boolean>;
  refetch: () => void;
}

function parse(row: Record<string, unknown>): ScBustNotification {
  return {
    id: String(row.id ?? ""),
    chain_id: String(row.chain_id ?? ""),
    reserve_id: String(row.reserve_id ?? ""),
    session_id: String(row.session_id ?? ""),
    bust_game: Number(row.bust_game ?? 0),
    stake_lost: Number(row.stake_lost ?? 0),
    stake_source: (row.stake_source as "capital" | "profit") ?? "profit",
    capital_after: Number(row.capital_after ?? 0),
    profit_after: Number(row.profit_after ?? 0),
    message: String(row.message ?? ""),
    cooldown_ends_at: typeof row.cooldown_ends_at === "string" ? row.cooldown_ends_at : null,
    created_at: String(row.created_at ?? ""),
    acknowledged_at: typeof row.acknowledged_at === "string" ? row.acknowledged_at : null,
  };
}

export function useBustNotifications(): UseBustNotificationsState {
  const { user } = useAuthSession();
  const [notifications, setNotifications] = useState<readonly ScBustNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!user) {
      setNotifications([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    const client = getBrowserClient();
    setLoading(true);
    setError(null);

    void (async () => {
      try {
        const { data, error: qError } = await (
          (client as unknown as {
            from: (t: string) => {
              select: (cols: string) => {
                eq: (col: string, val: string) => {
                  is: (col: string, val: null) => {
                    order: (col: string, opts: { ascending: boolean }) => {
                      limit: (n: number) => Promise<{
                        data: Record<string, unknown>[] | null;
                        error: { message: string } | null;
                      }>;
                    };
                  };
                };
              };
            };
          }).from("sc_bust_notifications")
        )
          .select("*")
          .eq("user_id", user.id)
          .is("acknowledged_at", null)
          .order("created_at", { ascending: false })
          .limit(10);

        if (cancelled) return;
        if (qError) {
          setError(qError.message);
          setLoading(false);
          return;
        }
        setNotifications((data ?? []).map(parse));
        setLoading(false);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [user, tick]);

  const acknowledge = useCallback(async (id: string): Promise<boolean> => {
    const client = getBrowserClient();
    try {
      const { error: rpcError } = await (
        client.rpc as unknown as (
          fn: string,
          args: Record<string, unknown>,
        ) => Promise<{ data: unknown; error: { message: string } | null }>
      )("acknowledge_bust_notification", { p_notification_id: id });

      if (rpcError) {
        setError(rpcError.message);
        return false;
      }
      // Optimistic strip + refetch.
      setNotifications((cur) => cur.filter((n) => n.id !== id));
      refetch();
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      return false;
    }
  }, [refetch]);

  return useMemo(
    () => ({ notifications, loading, error, acknowledge, refetch }),
    [notifications, loading, error, acknowledge, refetch],
  );
}
